import React, { useState, useRef, useEffect } from 'react';
import qs from "qs";
import api from '../api';
import { venueDetailsMapping } from '../misc/venueDetailsMapping';
import DangerIcon from '../assets/modal-icon-danger.png';
import '../styles/TagsMatching.css';
import '../styles/buttons.css';

const TagsMatching = ({
  user_id,
  accountStatus,
  selectedVenues,
  setSelectedVenues,
  setTagFilteredVenues,
}) => {
  const options = [
    { value: 'Garden', label: 'Garden' },
    { value: 'Mountain', label: 'Mountain View' },
    { value: 'Ocean', label: 'Ocean View' },
    { value: 'Desert', label: 'Desert View' },
    { value: 'Winery', label: 'Winery' },
    { value: 'National Park', label: 'National Park' },
    { value: 'Ranch', label: 'Ranch' },
    { value: 'Golf Club', label: 'Golf Club' },
    { value: 'Luxury Hotel', label: 'Luxury Hotel' },
    { value: 'Landmark Hotel', label: 'Landmark Hotel' },
    { value: 'City Hall', label: 'City Hall' },
  ];

  const [selectedTags, setSelectedTags] = useState([]);
  const [venuesData, setVenuesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // keep an internal copy of selectedVenues so clicks “stick” even in tests
  const [localSelectedVenues, setLocalSelectedVenues] = useState(selectedVenues);
  // sync if parent updates
  useEffect(() => {
    setLocalSelectedVenues(selectedVenues);
  }, [selectedVenues]);

  const dropdownRef = useRef(null);
  const venuesRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !dropdownRef.current?.contains(e.target) &&
        !venuesRef.current?.contains(e.target)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch venues when tags change
  useEffect(() => {
    const fetchVenues = async () => {
      if (selectedTags.length === 0) {
        setVenuesData([]);
        setTagFilteredVenues([]);
        return;
      }

      setIsLoading(true);
      setApiError(null);

      try {
        const { data } = await api.get('/search_venues_by_tags', {
          params: { tags: selectedTags },
          paramsSerializer: (params) =>
            qs.stringify(params, { arrayFormat: "repeat" }),
        });

        if (data.status === 'success') {
          setVenuesData(data.venues);
          setTagFilteredVenues(data.venues);
        } else {
          setApiError(data.message || 'Error fetching venues.');
        }
      } catch (err) {
        console.error('Error fetching venues by tags:', err);
        setApiError('Error fetching venues. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchVenues();
  }, [selectedTags, setTagFilteredVenues]);

  const handleTagToggle = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const getMaxAllowed = () => {
    const plan = accountStatus?.subscription.plan;
    return plan === 'trial' || plan === 'essentials' ? 1 : Infinity;
  };

  const handleVenueSelection = (name) => {
    const max = getMaxAllowed();

    if (!localSelectedVenues.includes(name)) {
      if (localSelectedVenues.length >= max) {
        setModalMessage(
          'Due to your current plan, you can only select one venue. Upgrade to view more.'
        );
        setShowModal(true);
        return;
      }
      const newSel = [...localSelectedVenues, name];
      setLocalSelectedVenues(newSel);
      setSelectedVenues(newSel);
    } else {
      const newSel = localSelectedVenues.filter((n) => n !== name);
      setLocalSelectedVenues(newSel);
      setSelectedVenues(newSel);
    }
  };

  return (
    <div className="tags-matching-page-container">
      <div className="tags-matching-container">
        <div className="tags-matching-header">
          <h1>Choose Your Dream Venue for Wedding</h1>
          <p>Select the venue types you’d love most.</p>
        </div>

        <div
          className={`custom-tags-selector ${isDropdownOpen ? 'open' : ''}`}
          ref={dropdownRef}
          id="tm-selector"
        >
          <div
            className="selected-tags-field"
            onClick={() => setIsDropdownOpen((o) => !o)}
          >
            {selectedTags.length ? (
              <div className="pill-container">
                {selectedTags.map((tag) => (
                  <span
                    key={tag}
                    className="pill selected"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTagToggle(tag);
                    }}
                  >
                    {options.find((o) => o.value === tag)?.label || tag}
                  </span>
                ))}
              </div>
            ) : (
              <span className="placeholder">Select venue types...</span>
            )}
            <span className="dropdown-arrow">&#9662;</span>
          </div>

          {isDropdownOpen && (
            <div className="options-container">
              <div className="pill-container">
                {options.map((opt) => (
                  <span
                    key={opt.value}
                    id={opt.value === 'Garden' ? 'tm-pill-garden' : undefined}
                    className={`pill ${
                      selectedTags.includes(opt.value) ? 'selected' : ''
                    }`}
                    onClick={() => handleTagToggle(opt.value)}
                  >
                    {opt.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {isLoading && <p>Loading venues...</p>}
      {apiError && <p className="error">{apiError}</p>}

      {venuesData.length > 0 && (
        <div className="venues-bucket" id="tm-venues" ref={venuesRef}>
          {venuesData.map((venue, i) => {
            const det = venueDetailsMapping[venue.name];
            if (!det) return null;
            return (
              <div
                key={venue.name}
                className={`venue-card ${
                  localSelectedVenues.includes(venue.name) ? 'selected' : ''
                }`}
                id={i === 0 ? 'tm-venue-card' : undefined}
                onClick={() => handleVenueSelection(venue.name)}
              >
                {venue.tags?.length > 0 && (
                  <div className="venue-tags-container">
                    {venue.tags.map((tag) => (
                      <span key={tag} className="pill">
                        {options.find((o) => o.value === tag)?.label || tag}
                      </span>
                    ))}
                  </div>
                )}
                <img
                  src={det.imageUrl}
                  alt={det.title}
                  className="venue-image"
                />
                <div className="card-content">
                  <h3 className="venue-title">{det.title}</h3>
                  <p className="venue-caption">{det.caption}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <img src={DangerIcon} alt="Danger" className="danger-icon" />
            <p>{modalMessage}</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsMatching;
